import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IEvent, IProgram } from "../../../interfaces/models/IEvent";
import {
  formatDayAndMonth,
  getLocalTimeFromTimestamp,
} from "../../../utils/dates";
import { upsertProgramSession } from "../../../utils/programVenue";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Eye from "../../../components/Atomos/Icons/ViewMode/Eye";
import Pencil from "../../../components/Atomos/Icons/ViewMode/Pencil";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import { UseGroupList } from "../../../hooks/Catalogs/useGroupList";
import SessionFormModal from "./SessionFormModal";
import PostEventPreview from "./PostEventPreview";
import { updateEventPrograms } from "../../../utils/updateEventPrograms";
import { toastError, toastPromise } from "../../../utils/toast";
import { NewEvent } from "../../../redux/states/event.state";
import PreviewComponente from "../../../components/SlateEditor/Preview";
import CircularPhoto from "../../../components/Atomos/CircularPhoto/CircularPhoto";
import { SpeakerTypeEnum } from "../../../Enums/EventEnums";
import { Appstore } from "../../../redux/store";
import MemberProgramTab from "./MemberProgramTab";
import { useEnrichedPrograms } from "../../../hooks/Events/useEnrichedPrograms";

const parseDescription = (description: string | undefined): any[] | null => {
  if (!description) {
    return null;
  }

  try {
    const parsed =
      typeof description === "string" ? JSON.parse(description) : description;
    if (Array.isArray(parsed)) {
      return parsed;
    }
    if (typeof parsed === "string") {
      return [{ type: "paragraph", children: [{ text: parsed }] }];
    }
    return null;
  } catch (e) {
    return [{ type: "paragraph", children: [{ text: description }] }];
  }
};

const organizeSpeakers = (speakers: any[]) => {
  const firstRow: any[] = []; // speaker, guest, moderator
  const panelists: any[] = [];
  const others: any[] = [];

  speakers.forEach((speaker) => {
    const type = speaker.speakerType;
    if (type === "speaker" || type === "guest" || type === "moderator") {
      firstRow.push(speaker);
    } else if (type === "panelist") {
      panelists.push(speaker);
    } else {
      others.push(speaker);
    }
  });

  return { firstRow, panelists, others };
};

export default function StaffProgramTab({
  event: eventProp,
  isEditMode: externalIsEditMode,
  onEditClick,
  onEventUpdate,
}: {
  event: IEvent | null;
  isEditMode?: boolean;
  onEditClick?: () => void;
  onEventUpdate?: (updatedEvent: IEvent) => void;
}) {
  const dispatch = useDispatch();
  const reduxEvent = useSelector((state: Appstore) => state.event);
  
  const [localEvent, setLocalEvent] = useState<IEvent | null>(eventProp);
  
  const programHash = useMemo(() => {
    if (!reduxEvent?.program) {
      return "";
    }
    return reduxEvent.program
      .map(p => `${p.title || ""}-${p.startTime || ""}-${p.endTime || ""}-${p.description || ""}`)
      .join("|");
  }, [reduxEvent?.program]);
  
  useEffect(() => {
    if (reduxEvent?.id && eventProp?.id && reduxEvent.id === eventProp.id) {
      setLocalEvent(reduxEvent);
    } else if (reduxEvent?.id && reduxEvent.id !== "") {
      setLocalEvent(reduxEvent);
    } else if (eventProp) {
      setLocalEvent(eventProp);
    }
  }, [reduxEvent, programHash, eventProp]);
  
  const event = localEvent;
  const enrichedPrograms = useEnrichedPrograms(event?.program);

  const isEditMode =
    externalIsEditMode !== undefined ? externalIsEditMode : false;
  const { firmData } = UseFirmList();
  const { groupData } = UseGroupList();
  const [firmLogos, setFirmLogos] = useState<
    {
      id: string;
      logo: string | null;
      smallLogo?: string | null;
      name: string;
    }[]
  >([]);
  const [groupLogos, setGroupLogos] = useState<
    {
      id: string;
      logo: string | null;
      smallLogo?: string | null;
      name: string;
    }[]
  >([]);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [editingSession, setEditingSession] = useState<IProgram | null>(null);
  const [previewMode, setPreviewMode] = useState<"session" | "postEvent">(
    "session"
  );

  useEffect(() => {
    if (event?.sponsorHost && event.sponsorHost.length > 0) {
      const memberSponsors = event.sponsorHost.filter(
        (sponsor) => sponsor.type === "member"
      );
      if (memberSponsors.length > 0) {
        if (firmData.length > 0) {
          const logos = memberSponsors.map((sponsor) => {
            const firm = firmData.find((f: any) => f.id === sponsor.id);
            return firm
              ? {
                  id: firm.id,
                  logo: firm.logo,
                  smallLogo: firm.smallLogo,
                  name: firm.name || firm.firmName,
                }
              : {
                  id: sponsor.id,
                  logo: null,
                  smallLogo: null,
                  name: "Unknown Firm",
                };
          });
          setFirmLogos(logos);
        } else {
          const placeholders = memberSponsors.map((sponsor) => ({
            id: sponsor.id,
            logo: null,
            smallLogo: null,
            name: "",
          }));
          setFirmLogos(placeholders);
        }
      } else {
        setFirmLogos([]);
      }

      const groupSponsors = event.sponsorHost.filter(
        (sponsor) => sponsor.type === "wsg_group"
      );
      if (groupSponsors.length > 0) {
        if (groupData.length > 0) {
          const logos = groupSponsors.map((sponsor) => {
            const group = groupData.find((g: any) => g.id === sponsor.id);
            return group
              ? {
                  id: group.id || sponsor.id,
                  logo: group.icon || null,
                  smallLogo: group.icon || null,
                  name: group.title || "Unknown Group",
                }
              : {
                  id: sponsor.id,
                  logo: null,
                  smallLogo: null,
                  name: "Unknown Group",
                };
          });
          setGroupLogos(logos);
        } else {
          const placeholders = groupSponsors.map((sponsor) => ({
            id: sponsor.id,
            logo: null,
            smallLogo: null,
            name: "",
          }));
          setGroupLogos(placeholders);
        }
      } else {
        setGroupLogos([]);
      }
    } else {
      setFirmLogos([]);
      setGroupLogos([]);
    }
  }, [event?.sponsorHost, firmData, groupData]);

  const [prevIsEditMode, setPrevIsEditMode] = useState(isEditMode);

  useEffect(() => {
    if (prevIsEditMode && !isEditMode && showSessionForm) {
      setShowSessionForm(false);
      setEditingSession(null);
    }

    if (isEditMode && !prevIsEditMode && !showSessionForm && !editingSession) {
      setEditingSession(null);
      setShowSessionForm(true);
    }

    setPrevIsEditMode(isEditMode);
  }, [isEditMode, showSessionForm, prevIsEditMode, editingSession]);

  if (!event) {
    return <div>Event not found</div>;
  }

  const hasPostEventInfo = () => {
    if (!event?.program) {
      return false;
    }
    return event.program.some( 
      (session) =>
        session.isPostEvent &&
        (session.postEventDescription ||
          session.youtubeLink ||
          (session.takeaways &&
            session.takeaways.length > 0 &&
            session.takeaways.some((t) => t.trim() !== "")))
    );
  };

  const handlePreviewToggle = () => {
    if (previewMode === "session") {
      if (hasPostEventInfo()) {
        setPreviewMode("postEvent");
      } else {
        toastError(
          "No post event session information available. Please add post event information to sessions to see the preview."
        );
      }
    } else {
      setPreviewMode("session");
    }
  };

  const handleEditSession = (program: IProgram) => {
    setEditingSession(program);
    setShowSessionForm(true);
    if (onEditClick && !isEditMode) {
      onEditClick();
    }
  };

  const handleCloseSessionForm = () => {
    setShowSessionForm(false);
    setEditingSession(null);
  };

  const handleSaveSession = async (session: Partial<IProgram>) => {
    if (!event || !event.id) {
      toastError("Event ID is required to save changes");
      return;
    }

    const isEmpty = !session.title && !session.startTime && !session.endTime;

    if (isEmpty && !editingSession) {
      handleCloseSessionForm();
      if (onEditClick && isEditMode) {
        onEditClick();
      }
      return;
    }

    const eventId = event.id;
    const editingSessionSnapshot = editingSession;
    const savePromise = (async () => {
      const reloadedEvent = await updateEventPrograms(
        eventId,
        (currentPrograms) =>
          upsertProgramSession(
            currentPrograms,
            session as IProgram,
            editingSessionSnapshot,
          )
      );

      dispatch(NewEvent(reloadedEvent));
      setLocalEvent(reloadedEvent);

      if (onEventUpdate) {
        onEventUpdate(reloadedEvent);
      }

      await new Promise((resolve) => setTimeout(resolve, 0));

      handleCloseSessionForm();

      if (onEditClick && isEditMode) {
        onEditClick();
      }
    })();

    try {
      await toastPromise(savePromise, {
        pending: "Saving session...",
        success: "Session saved successfully!",
        error: "Failed to save session",
      });
    } catch (error) {
      toastError(error as string);
    }
  };

  const handleDeleteSession = async () => {
    if (!editingSession) {
      handleCloseSessionForm();
      if (onEditClick && isEditMode) {
        onEditClick();
      }
      return;
    }

    if (!event || !event.id) {
      toastError("Cannot delete session");
      return;
    }

    const eventId = event.id;
    const editingSessionSnapshot = editingSession;

    try {
      const deletePromise = updateEventPrograms(eventId, (currentPrograms) =>
        currentPrograms.filter(
          (p) =>
            !(
              p.title === editingSessionSnapshot.title &&
              p.startTime === editingSessionSnapshot.startTime &&
              p.endTime === editingSessionSnapshot.endTime
            )
        )
      );

      const reloadedEvent = await toastPromise(deletePromise, {
        pending: "Deleting session...",
        success: "Session deleted successfully!",
        error: "Failed to delete session",
      });

      dispatch(NewEvent(reloadedEvent));
      setLocalEvent(reloadedEvent);

      if (onEventUpdate) {
        onEventUpdate(reloadedEvent);
      }

      handleCloseSessionForm();

      if (onEditClick && isEditMode) {
        onEditClick();
      }
    } catch (error) {
      toastError(error as string);
    }
  };

  if (showSessionForm) {
    return (
      <SessionFormModal
        onClose={handleCloseSessionForm}
        onSave={handleSaveSession}
        onDelete={handleDeleteSession}
        initialSession={editingSession}
        eventStartDate={event?.startDate}
        eventEndDate={event?.endDate}
        eventId={event?.id}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col items-start self-stretch">
        <div className="w-full">
          <div className="flex flex-row justify-between items-center w-full mb-[10px]">
            <h2 className="text-primary font-decimal text-[32px] font-medium leading-[36px]">
              {previewMode === "session"
                ? "Session Preview"
                : "Post Event Preview"}
            </h2>
            <LegacyButton
              text={
                previewMode === "session"
                  ? "POST EVENT PREVIEW"
                  : "SESSION PREVIEW"
              }
              content="textIcon"
              icon={
                <div className="w-5 h-5 [&>svg]:w-full [&>svg]:h-full [&>svg>g>g>path]:fill-current">
                  <Eye />
                </div>
              }
              color="accent"
              style="filled"
              size="small"
              iconOrder="textFirst"
              className="[&>div]:!justify-between [&>div]:!gap-2 [&>div]:!pl-0"
              onClick={handlePreviewToggle}
            />
          </div>
        </div>
      </div>

      {previewMode === "session" ? (
        <MemberProgramTab />
      ) : (
        <PostEventPreview
          sessions={enrichedPrograms.length > 0 ? enrichedPrograms : (event?.program || [])}
          eventId={event?.id}
        />
      )}
    </>
  );
}
